<template>
  <teleport to="body">
    <div class="container-fluid">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-hdg mb-3 text-center">Party Runes</div>
            <hr class="bb-horizontal-divider bb-tabs-bottom-divider" />

            <div class="row align-items-start mb-1">
              <div class="col">
                <ul
                  class="nav nav-pills mb-1 bb-item-tabs"
                  role="tablist"
                >
                  <li
                    class="nav-item"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="nav-link me-1"
                      :class="{ active: activeTab === 'runes' }"
                      @click="activeTab = 'runes'"
                    >
                      Runes
                    </button>
                  </li>
                  <li
                    class="nav-item"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="nav-link me-1"
                      :class="{ active: activeTab === 'counts' }"
                      @click="activeTab = 'counts'"
                    >
                      Counts
                    </button>
                  </li>
                </ul>
              </div>

              <div class="col-auto">
                <div class="bb-popup-table-actions">
                  <button
                    type="button"
                    class="btn btn-lg btn-primary"
                    @click="handleClose"
                  >
                    Ok
                  </button>
                </div>
              </div>
            </div>

            <hr class="bb-horizontal-divider" />

            <div
              v-if="errorMessage"
              class="alert alert-danger mb-3"
            >
              {{ errorMessage }}
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <PartyRunesTable
              v-else-if="activeTab === 'runes'"
              v-model:searchTerm="searchTerm"
              :partyCombatants="partyCombatants"
              :npcCombatants="npcCombatants"
              :rows="rows"
              :emptyMessage="tableEmptyMessage"
              :searchEnabled="hasCharacters"
            />

            <div
              v-else
              class="bb-table-wrapper mt-3 bb-popup-table-table-wrapper"
            >
              <vue-good-table
                v-if="!countsBlockedMessage"
                :columns="countsTableColumns"
                :rows="countsTableRows"
                :pagination-options="countsPaginationOptions"
                :row-style-class="rowStyleClassFn"
                styleClass="vgt-table bordered"
                theme="black-rhino"
              >
                <template #table-column="props">
                  <span v-if="props.column.field === 'count_label'">
                    {{ props.column.label }}
                  </span>
                  <div
                    v-else
                    class="bb-viewer-column-heading"
                  >
                    <span class="bb-viewer-column-name">
                      {{ props.column.label }}
                    </span>
                    <span class="bb-viewer-column-subtitle">
                      {{ props.column.playerLabel }}
                    </span>
                  </div>
                </template>

                <template #table-row="props">
                  <span>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>

                <template #emptystate></template>
              </vue-good-table>

              <div
                v-else
                class="bb-empty-state"
              >
                {{ countsBlockedMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'

import PartyRunesTable from '../components/PartyRunesTable.vue'

export default {
  name: 'PartyRunesView',
  props: {
    fallbackArenaName: {
      type: String,
      default: '',
    },
    fallbackArenaSlug: {
      type: String,
      default: '',
    },
  },
  components: {
    PartyRunesTable,
    VueGoodTable,
  },
  data() {
    return {
      activeTab: 'runes',
      allRows: [],
      searchTerm: '',
      isLoading: false,
      isLookupScheduled: false,
      errorMessage: '',
      partyCombatants: [],
      npcCombatants: [],
      resolvedArenaSlug: '',
      resolvedArenaName: '',
      rows: [],
      pendingLookupHandle: null,
      latestLookupId: 0,
    }
  },
  computed: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
    }),
    ...mapGetters('characters', {
      currentCharacter: 'getCurrentCharacter',
    }),
    hasArenaContext() {
      return Boolean(
        this.resolvedArenaSlug ||
          this.$route.params.slug ||
          this.$route.query.slug ||
          this.fallbackArenaSlug ||
          this.currentCharacter?.arena_slug ||
          this.currentCharacter?.campaign_slug ||
          this.getCampaign?.slug ||
          this.resolvedArenaName ||
          this.fallbackArenaName ||
          this.currentCharacter?.arena_name ||
          this.getCampaign?.name,
      )
    },
    arenaSlug() {
      return (
        this.resolvedArenaSlug ||
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.getCampaign?.slug ||
        ''
      )
    },
    minimumLookupLength() {
      return 3
    },
    allCombatants() {
      return [...this.partyCombatants, ...this.npcCombatants]
    },
    hasCharacters() {
      return this.partyCombatants.length > 0 || this.npcCombatants.length > 0
    },
    tableEmptyMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      const trimmedSearch = this.searchTerm.trim()
      if (!trimmedSearch) {
        return '*** No Character Runes for this Campaign ***'
      }

      if (trimmedSearch.length < this.minimumLookupLength) {
        return `Enter at least ${this.minimumLookupLength} characters before lookup runs.`
      }

      if (this.isLoading || this.isLookupScheduled) {
        return 'Loading matching runes...'
      }

      if (this.rows.length > 0) {
        return ''
      }

      return `No runes matched "${trimmedSearch}".`
    },
    countsBlockedMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      if (!this.hasCharacters) {
        return 'No Alive Party Characters/NPCs'
      }

      return ''
    },
    countsTableColumns() {
      return [
        {
          label: 'Rune Counts',
          field: 'count_label',
          width: '8.5rem',
        },
        ...this.partyCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'No player',
          width: '6rem',
        })),
        ...this.npcCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'NPC',
          width: '6rem',
        })),
      ]
    },
    countsTableRows() {
      const runeStates = ['Active', 'Integrated', 'Owned']

      return runeStates.map((stateLabel) => {
        const row = {
          count_label: stateLabel,
        }

        this.allCombatants.forEach((combatant) => {
          row[this.getCombatantField(combatant)] = this.countRunesForCombatant(
            combatant,
            stateLabel,
          )
        })

        return row
      })
    },
    countsPaginationOptions() {
      return {
        enabled: false,
        mode: 'records',
        perPage: 3,
        position: 'bottom',
        dropdownAllowAll: false,
      }
    },
  },
  watch: {
    searchTerm: {
      handler(newValue) {
        this.scheduleLookup(newValue)
      },
    },
  },
  mounted() {
    this.fetchRuneMatrix('')
  },
  beforeUnmount() {
    this.clearPendingLookup()
  },
  methods: {
    getCombatantField(combatant) {
      return `character_${combatant.character_id}`
    },
    extractRuneState(value) {
      return String(value || '')
        .split(' (')[0]
        .trim()
    },
    countRunesForCombatant(combatant, stateLabel) {
      const characterId = String(combatant?.character_id || '')
      if (!characterId) {
        return 0
      }

      return this.rows.reduce((total, row) => {
        const state = this.extractRuneState(row?.values?.[characterId])
        return total + (state === stateLabel ? 1 : 0)
      }, 0)
    },
    rowStyleClassFn(row) {
      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      }

      return 'rowParty'
    },
    clearPendingLookup() {
      if (this.pendingLookupHandle) {
        window.clearTimeout(this.pendingLookupHandle)
        this.pendingLookupHandle = null
      }

      this.isLookupScheduled = false
    },
    scheduleLookup(value) {
      this.clearPendingLookup()
      this.errorMessage = ''

      const trimmedValue = (value || '').trim()

      if (!trimmedValue || trimmedValue.length < this.minimumLookupLength) {
        this.rows = this.allRows
        this.isLoading = false
        return
      }

      this.isLookupScheduled = true

      this.pendingLookupHandle = window.setTimeout(() => {
        this.isLookupScheduled = false
        this.fetchRuneMatrix(trimmedValue)
      }, 450)
    },
    async resolveArenaSlug() {
      const directSlug =
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.getCampaign?.slug ||
        ''

      if (directSlug) {
        this.resolvedArenaSlug = directSlug
        this.resolvedArenaName =
          this.resolvedArenaName ||
          this.fallbackArenaName ||
          this.currentCharacter?.arena_name ||
          this.getCampaign?.name ||
          ''
        return directSlug
      }

      const targetArenaName = String(
        this.fallbackArenaName ||
          this.currentCharacter?.arena_name ||
          this.getCampaign?.name ||
          '',
      ).trim()

      if (!targetArenaName) {
        return ''
      }

      const headers = this.getHeaders || {}
      const endpoints = []

      if (this.$route.name === 'myCharactersView') {
        endpoints.push('api/v1/campaigns/member/')
      }

      endpoints.push('api/v1/campaigns/my/')
      endpoints.push('api/v1/campaigns/list/')

      for (const initialEndpoint of endpoints) {
        let nextUrl = initialEndpoint

        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            headers,
            mode: 'same-origin',
          })
          const results = Array.isArray(response?.data?.results)
            ? response.data.results
            : []
          const campaign = results.find(
            (entry) => String(entry?.name || '') === targetArenaName,
          )

          if (campaign?.slug) {
            this.resolvedArenaSlug = campaign.slug
            this.resolvedArenaName = campaign.name || targetArenaName
            return campaign.slug
          }

          nextUrl = response?.data?.next ?? null
        }
      }

      return ''
    },
    async fetchRuneMatrix(query) {
      if (!this.hasArenaContext) {
        this.errorMessage = 'No active combat campaign is loaded.'
        this.rows = []
        return
      }

      const resolvedArenaSlug = await this.resolveArenaSlug()
      if (!resolvedArenaSlug) {
        this.errorMessage = 'No active combat campaign is loaded.'
        this.rows = []
        return
      }

      const lookupId = ++this.latestLookupId
      this.isLoading = true

      try {
        const response = await axios.get(
          'api/v1/character-runes/party-viewer/',
          {
            params: {
              arena_slug: resolvedArenaSlug,
              q: query,
            },
          },
        )

        if (lookupId !== this.latestLookupId) {
          return
        }

        this.partyCombatants = response.data.party_combatants || []
        this.npcCombatants = response.data.npc_combatants || []
        this.rows = response.data.rows || []

        if (!query) {
          this.allRows = this.rows
        }
      } catch (error) {
        if (lookupId !== this.latestLookupId) {
          return
        }

        this.rows = []
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.arena_slug?.[0] ||
          'Unable to load combat runes.'
      } finally {
        if (lookupId === this.latestLookupId) {
          this.isLoading = false
        }
      }
    },
    handleClose() {
      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-item-tabs {
  gap: 0.1rem;
}

.bb-item-tabs .nav-link {
  margin-right: 0 !important;
  padding: 0.9rem 1rem;
}

.bb-popup-table-card {
  width: min(72rem, 95vw);
  height: min(96vh, 78rem);
  max-height: min(96vh, 78rem);
  position: relative;
  overflow: hidden;
  margin-left: auto;
  margin-right: 1.5rem;
}

.bb-tabs-bottom-divider {
  margin-top: 0.1rem;
}

.bb-popup-table-body {
  padding: 2rem 2rem 1rem !important;
}

.bb-popup-table-table-wrapper {
  height: 64vh;
  max-height: 64vh;
  padding: 0.2rem 0.2rem 1rem;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
}

.bb-popup-table-actions .btn {
  width: auto;
  min-width: 8rem;
  margin-bottom: 0;
}

.bb-popup-table-loader {
  margin-bottom: 1rem;
}

.bb-viewer-column-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
}

.bb-viewer-column-name {
  color: white;
  font-weight: inherit;
  font-size: inherit;
  line-height: 1.2;
}

.bb-viewer-column-subtitle {
  color: #737373;
  font-weight: 400;
  line-height: 1.2;
}

.bb-empty-state {
  padding: 0.5rem 0 0;
  color: white;
  font-size: 1.3rem;
}

:deep(.vgt-table) {
  min-width: 50rem;
  table-layout: fixed;
}

:deep(.vgt-table thead th) {
  vertical-align: bottom;
  white-space: normal;
  padding-top: 10px;
  padding-bottom: 8px;
}

:deep(.vgt-table thead th:first-child),
:deep(.vgt-table tbody td:first-child) {
  width: 8.5rem;
  min-width: 8.5rem;
  max-width: 8.5rem;
}

:deep(.vgt-table thead th:not(:first-child)),
:deep(.vgt-table tbody td:not(:first-child)) {
  width: 6rem;
}

:deep(.vgt-table tbody td),
:deep(.vgt-table tbody th) {
  vertical-align: middle;
}

:deep(.vgt-table tbody td:first-child) {
  font-weight: 700;
  color: #c2ccd6;
}

:deep(.vgt-table tbody td:not(:first-child)) {
  text-align: center;
  font-weight: 600;
}

:deep(.vgt-wrap__footer) {
  border-top: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

@media (max-width: 768px) {
  .bb-popup-table-card {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    height: calc(100vh - 1rem);
    margin-right: 0.5rem;
  }

  .bb-popup-table-body {
    padding: 1.25rem 1.25rem 1rem !important;
  }

  .bb-popup-table-actions {
    justify-content: flex-start;
  }
}
</style>
